// src/components/ClientDetailsScreen.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock3,
  Edit3,
  Eye,
  FolderOpen,
  History,
  Mail,
  MapPin,
  Menu,
  NotebookPen,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/clientDetails.scss";
import { useToast } from "../context/ToastContext";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLANO: "plano",
};

const EVOLUTIONS_PER_PAGE = 6;


function readAuth(key) {
  if (key === AUTH_KEYS.TOKEN) {
    return (
      sessionStorage.getItem("psy_token") ||
      localStorage.getItem("psy_token") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("token") ||
      ""
    );
  }

  if (key === AUTH_KEYS.USER_ID) {
    return (
      sessionStorage.getItem("psy_id") ||
      localStorage.getItem("psy_id") ||
      sessionStorage.getItem("id") ||
      localStorage.getItem("id") ||
      ""
    );
  }

  if (key === AUTH_KEYS.USER_NAME) {
    return (
      sessionStorage.getItem("psy_usuario") ||
      localStorage.getItem("psy_usuario") ||
      sessionStorage.getItem("nome") ||
      localStorage.getItem("nome") ||
      ""
    );
  }

  if (key === AUTH_KEYS.PLANO) {
    return (
      sessionStorage.getItem("plano") ||
      localStorage.getItem("plano") ||
      sessionStorage.getItem("psy_plano") ||
      localStorage.getItem("psy_plano") ||
      ""
    );
  }

  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function saveNewToken(response) {
  const newToken = response.headers.get("x-new-token");

  if (newToken) {
    localStorage.setItem(AUTH_KEYS.TOKEN, newToken);
    sessionStorage.setItem(AUTH_KEYS.TOKEN, newToken);
  }
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function initials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "CL";

  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";

  return `${first}${last}`.toUpperCase() || "CL";
}

function formatCPF(cpf) {
  const digits = onlyDigits(cpf);

  if (!digits) return "Não informado";

  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
      6,
      9
    )}-${digits.slice(9)}`;
  }

  return String(cpf || "Não informado");
}

function capitalizeFirst(value = "") {
  const text = String(value || "").trim();
  if (!text) return "Não informado";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function maskCPF(value) {
  const digits = onlyDigits(value);
  if (!digits) return "Não informado";
  if (digits.length !== 11) return "Não informado";
  return `***.***.***-${digits.slice(-2)}`;
}

function appointmentStatusLabel(value = "") {
  const status = String(value || "").trim().toLowerCase();

  const labels = {
    pendente: "Pendente",
    confirmado: "Confirmado",
    aguardando_confirmacao: "Aguardando confirmação",
    cancelado: "Cancelado",
    baixado: "Encerrado",
    expirado: "Expirado",
    no_show: "Não compareceu",
  };

  return labels[status] || capitalizeFirst(status || "pendente");
}

function formatDocument(documento) {
  const digits = onlyDigits(documento);

  if (!digits) return "Não informado";

  if (digits.length === 11) return formatCPF(digits);

  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(
      5,
      8
    )}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }

  return String(documento || "Não informado");
}

function formatDateBR(dateLike) {
  if (!dateLike) return "Não registrado";

  try {
    const date = new Date(dateLike);

    if (Number.isNaN(date.getTime())) {
      return String(dateLike);
    }

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch {
    return "Formato inválido";
  }
}

/*
 * Datas cadastrais como nascimento/início/fim de terapia representam
 * um dia de calendário, não um instante de tempo. O Mongo costuma
 * devolvê-las como 00:00 UTC; converter diretamente para o fuso do
 * navegador pode fazer 27/07 aparecer como 26/07 no Brasil.
 */
function formatDateOnlyBR(dateLike) {
  if (!dateLike) return "Não registrado";

  const raw = String(dateLike).trim();
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDate) {
    return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
  }

  try {
    const date = new Date(dateLike);

    if (Number.isNaN(date.getTime())) {
      return String(dateLike);
    }

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return "Formato inválido";
  }
}

function formatDateTimeBR(dateLike) {
  if (!dateLike) return "Não registrado";

  try {
    const date = new Date(dateLike);

    if (Number.isNaN(date.getTime())) {
      return String(dateLike);
    }

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "Formato inválido";
  }
}

function getEmergencyContacts(client) {
  const list =
    client?.contatosEmergencia ||
    client?.contatosDeEmergencia ||
    client?.emergencyContacts ||
    client?.contatoEmergencia ||
    [];

  if (!Array.isArray(list)) {
    return list ? [list] : [];
  }

  return list.filter((item) =>
    Object.values(item || {}).some((value) => String(value || "").trim())
  );
}

function getEmergencyContactTitle(contact, index) {
  return (
    contact?.nome ||
    contact?.nomeContato ||
    contact?.contato ||
    `Contato ${index + 1}`
  );
}

function getEmergencyContactPhone(contact) {
  return (
    contact?.telefone ||
    contact?.celular ||
    contact?.fone ||
    "Telefone não informado"
  );
}

function getEmergencyContactRelation(contact) {
  return (
    contact?.parentesco ||
    contact?.relacao ||
    contact?.vinculo ||
    contact?.grauParentesco ||
    "Vínculo não informado"
  );
}


function getTextPreview(value = "", max = 220) {
  const text = String(value || "").replace(/\s+/g, " ").trim();

  if (!text) return "";

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}...`;
}

function getEvolutionKey(item, fallback = "") {
  return String(item?._id || item?.id || item?.evolucaoId || fallback);
}

function normalizeErrorMessage(error, fallback) {
  return error?.message || fallback || "Não foi possível concluir a operação.";
}

async function readJsonSafe(response) {
  return await response.json().catch(() => null);
}

function normalizeCommunicationText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const CHANNEL_FAILURE_STATUS = new Set([
  "failed",
  "undelivered",
  "canceled",
  "cancelled",
  "error",
  "erro",
  "falhou",
]);

const CHANNEL_SUCCESS_STATUS = new Set([
  "created",
  "queued",
  "sent",
  "delivered",
  "read",
  "success",
  "sucesso",
  "ok",
  "enviado",
  "entregue",
  "lido",
  "encaminhado",
]);

function normalizeChannelKey(value = "") {
  const normalized = normalizeCommunicationText(value).replace(/[^a-z]/g, "");

  if (normalized.includes("whatsapp")) return "whatsapp";
  if (normalized.includes("email") || normalized.includes("mail")) return "email";

  return normalized;
}

function findChannelResult(payload, channel) {
  if (!payload || typeof payload !== "object") return null;

  const target = normalizeChannelKey(channel);

  const containers = [
    payload,
    payload?.resultados,
    payload?.resultado,
    payload?.canais,
    payload?.comunicacao,
    payload?.comunicacoes,
    payload?.envios,
    payload?.termo,
    payload?.lembrete,
    payload?.cobranca,
    payload?.recibo,
    payload?.termo?.resultados,
    payload?.lembrete?.resultados,
    payload?.cobranca?.resultados,
    payload?.recibo?.resultados,
    payload?.recibo?.comunicacao,
    payload?.recibo?.comunicacoes,
  ].filter(Boolean);

  for (const container of containers) {
    if (Array.isArray(container)) {
      for (const item of container) {
        if (!item || typeof item !== "object") continue;

        const nestedDirect =
          target === "whatsapp"
            ? item.whatsapp ||
              item.whatsApp ||
              item.resultadoWhatsapp ||
              item.whatsappResultado
            : item.email ||
              item.eMail ||
              item.resultadoEmail ||
              item.emailResultado;

        if (
          nestedDirect !== undefined &&
          nestedDirect !== null &&
          (typeof nestedDirect === "object" ||
            typeof nestedDirect === "boolean" ||
            CHANNEL_SUCCESS_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ) ||
            CHANNEL_FAILURE_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ))
        ) {
          return nestedDirect;
        }

        const key = normalizeChannelKey(
          item?.canal ||
            item?.channel ||
            item?.tipoCanal ||
            item?.tipo ||
            item?.nome
        );

        if (key === target) return item;
      }

      continue;
    }

    if (typeof container !== "object") continue;

    const direct =
      target === "whatsapp"
        ? container.whatsapp ||
          container.whatsApp ||
          container.resultadoWhatsapp ||
          container.whatsappResultado
        : container.email ||
          container.eMail ||
          container.resultadoEmail ||
          container.emailResultado;

    if (direct !== undefined && direct !== null) {
      return direct;
    }

    const ownKey = normalizeChannelKey(
      container?.canal ||
        container?.channel ||
        container?.tipoCanal
    );

    if (ownKey === target) {
      return container;
    }
  }

  return null;
}

function normalizeChannelResult(payload, channel) {
  const raw = findChannelResult(payload, channel);

  if (raw === null || raw === undefined) return null;

  if (typeof raw === "boolean") {
    return {
      channel,
      success: raw,
      failed: !raw,
      status: raw ? "sent" : "failed",
      error: "",
    };
  }

  if (typeof raw === "string") {
    const status = normalizeCommunicationText(raw).replace(/\s+/g, "_");

    return {
      channel,
      success: CHANNEL_SUCCESS_STATUS.has(status),
      failed: CHANNEL_FAILURE_STATUS.has(status),
      status,
      error: "",
    };
  }

  if (typeof raw !== "object") return null;

  const status = normalizeCommunicationText(
    raw.status ||
      raw.messageStatus ||
      raw.twilioStatus ||
      raw.situacao ||
      ""
  ).replace(/\s+/g, "_");

  const successFlag =
    raw.success ??
    raw.sucesso ??
    raw.ok ??
    raw.enviado ??
    raw.sent ??
    null;

  const requested =
    raw.solicitado ??
    raw.requested ??
    raw.processado ??
    true;

  const failed =
    requested !== false &&
    (successFlag === false || CHANNEL_FAILURE_STATUS.has(status));

  const success =
    requested !== false &&
    !failed &&
    (successFlag === true || CHANNEL_SUCCESS_STATUS.has(status));

  return {
    channel,
    success,
    failed,
    status,
    requested,
    error:
      raw.erroMensagem ||
      raw.erro ||
      raw.error ||
      raw.message ||
      "",
  };
}

function getChannelStatusLabel(result) {
  if (!result) return "";

  if (result.requested === false) return "Não solicitado";

  const status = normalizeCommunicationText(result.status).replace(/\s+/g, "_");

  if (result.failed) return "Falhou";

  if (result.channel === "whatsapp") {
    if (status === "read" || status === "lido") return "Lido";
    if (status === "delivered" || status === "entregue") return "Entregue";
    if (status === "sent" || status === "enviado") return "Enviado";
    if (
      status === "queued" ||
      status === "created" ||
      status === "encaminhado"
    ) {
      return "Encaminhado";
    }

    if (result.success) return "Encaminhado";
    return "Processando";
  }

  if (result.success) return "Enviado";
  if (result.failed) return "Falhou";

  return "Processando";
}

function buildCommunicationFeedback(payload, fallbackMessage = "") {
  const email = normalizeChannelResult(payload, "email");
  const whatsapp = normalizeChannelResult(payload, "whatsapp");
  const results = [email, whatsapp].filter(Boolean);

  if (!results.length) {
    return {
      hasChannels: false,
      tone: "success",
      message: fallbackMessage,
      details: [],
    };
  }

  const details = results.map((result) => {
    const label = result.channel === "whatsapp" ? "WhatsApp" : "E-mail";
    const statusLabel = getChannelStatusLabel(result);

    if (result.failed && result.error) {
      return `${label}: ${statusLabel} — ${result.error}`;
    }

    return `${label}: ${statusLabel}`;
  });

  const hasSuccess = results.some((item) => item.success);
  const hasFailure = results.some((item) => item.failed);

  return {
    hasChannels: true,
    tone: hasFailure ? (hasSuccess ? "warning" : "error") : "success",
    message: details.join(" • "),
    details,
  };
}



function ConfirmModal({
  open,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  danger = false,
  loading = false,
  onConfirm,
  onClose,
}) {
  if (!open) return null;

  return (
    <div className="cdl-modal-overlay" role="dialog" aria-modal="true">
      <div className="cdl-modal">
        <div
          className={`cdl-modal__icon ${
            danger ? "cdl-modal__icon--danger" : ""
          }`}
          aria-hidden="true"
        >
          {danger ? (
            <AlertTriangle size={24} />
          ) : (
            <CheckCircle2 size={24} />
          )}
        </div>

        <div className="cdl-modal__title">{title}</div>
        <div className="cdl-modal__msg">{message}</div>

        <div className="cdl-modal__actions">
          <button
            className="cdl-btn cdl-btn--ghost"
            type="button"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>

          <button
            className={`cdl-btn ${danger ? "cdl-btn--danger" : "cdl-btn--primary"}`}
            type="button"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Processando..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, wide = false }) {
  return (
    <div className={`cdl-row ${wide ? "cdl-row--wide" : ""}`}>
      <div className="cdl-row__label">{label}</div>
      <div className="cdl-row__value" title={String(value || "")}>
        {value || "Não informado"}
      </div>
    </div>
  );
}

function MetricCard({ label, value, hint, icon, variant = "blue" }) {
  return (
    <div className={`cdl-metric cdl-metric--${variant}`}>
      <div className="cdl-metric__icon">{icon}</div>
      <div className="cdl-metric__body">
        <div className="cdl-metric__label">{label}</div>
        <div className="cdl-metric__value">{value}</div>
        {hint ? <div className="cdl-metric__hint">{hint}</div> : null}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, description, actionLabel, onAction }) {
  return (
    <div className="cdl-empty">
      <div className="cdl-empty__icon">{icon}</div>
      <div className="cdl-empty__title">{title}</div>
      {description ? <div className="cdl-empty__desc">{description}</div> : null}

      {actionLabel && onAction ? (
        <button className="cdl-btn cdl-btn--primary cdl-empty__btn" type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function InsightsWidget({ title, insights }) {
  return (
    <div className="cdl-insights">
      <div className="cdl-insights__top">
        <div>
          <div className="cdl-insights__kicker">
            <Sparkles size={14} />
            PSYA
          </div>
          <div className="cdl-insights__title">{title}</div>
        </div>
        <span className="cdl-insights__badge">Leitura assistida</span>
      </div>

      <ul className="cdl-insights__list">
        {(insights || []).map((text, index) => (
          <li key={index}>{text}</li>
        ))}
      </ul>
    </div>
  );
}


function EvolutionDetailModal({ open, evolucao, numeroProntuario, onClose, onEdit, onDelete }) {
  if (!open || !evolucao) return null;

  const numeroEvolucao =
    evolucao?.numeroEvolucao != null
      ? `Evolução ${evolucao.numeroEvolucao}`
      : "Evolução clínica";

  return (
    <div className="cdl-modal-overlay cdl-evolution-modal" role="dialog" aria-modal="true">
      <div className="cdl-evolution-modal__panel">
        <div className="cdl-evolution-modal__head">
          <div>
            <span className="cdl-evolution-modal__kicker">Histórico clínico</span>
            <h2>{numeroEvolucao}</h2>
            <p>
              Prontuário {numeroProntuario || "não informado"} • {formatDateBR(evolucao?.dataAtendimento)}
            </p>
          </div>

          <button
            className="cdl-evolution-modal__close"
            type="button"
            onClick={onClose}
            aria-label="Fechar evolução"
          >
            <X size={20} />
          </button>
        </div>

        <div className="cdl-evolution-modal__meta">
          <div>
            <small>Número do prontuário</small>
            <strong>{numeroProntuario || "Não informado"}</strong>
          </div>
          <div>
            <small>Número da evolução</small>
            <strong>{evolucao?.numeroEvolucao || "Não informado"}</strong>
          </div>
          <div>
            <small>Data do atendimento</small>
            <strong>{formatDateBR(evolucao?.dataAtendimento)}</strong>
          </div>
          <div>
            <small>Origem</small>
            <strong>
              {evolucao?.origem === "sessao" || evolucao?.sessaoId
                ? "Sessão"
                : evolucao?.origem === "legado"
                  ? "Registro legado"
                  : "Manual"}
            </strong>
          </div>
        </div>

        <div className="cdl-evolution-modal__content">
          {evolucao?.resumoSessao ? (
            <section className="cdl-evolution-modal__summary">
              <h3>Resumo clínico da sessão</h3>
              <p>{evolucao.resumoSessao}</p>
              <small>
                Registro original realizado no encerramento da sessão.
              </small>
            </section>
          ) : null}

          <section>
            <h3>Observações clínicas</h3>
            <p>{evolucao?.observacoes || "Sem observações registradas."}</p>
          </section>

          <section>
            <h3>Recomendações</h3>
            <p>{evolucao?.recomendacoes || "Sem recomendações registradas."}</p>
          </section>
        </div>

        <div className="cdl-evolution-modal__actions">
          <button className="cdl-btn cdl-btn--ghost" type="button" onClick={onClose}>
            Fechar
          </button>

          <button
            className="cdl-btn cdl-btn--ghost"
            type="button"
            onClick={() => onEdit?.(evolucao)}
          >
            Editar evolução
          </button>

          <button
            className="cdl-btn cdl-btn--danger"
            type="button"
            onClick={() => onDelete?.(evolucao)}
          >
            Excluir evolução
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClientDetailsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [client, setClient] = useState(location.state?.clientData || null);

  const clientId = client?._id || client?.id || "";

  const [isPremium, setIsPremium] = useState(false);
  const [prontuarios, setProntuarios] = useState([]);
  const [anamneseInfo, setAnamneseInfo] = useState(null);
  const [loadingAnamnese, setLoadingAnamnese] = useState(false);
  const [clientAppointments, setClientAppointments] = useState([]);
  const [loadingClientAppointments, setLoadingClientAppointments] = useState(false);
  const [appointmentsPagination, setAppointmentsPagination] = useState({
    total: 0,
    offset: 0,
    limit: 5,
    visibleFrom: 0,
    visibleTo: 0,
    hasPrevious: false,
    hasNext: false,
    previousOffset: null,
    nextOffset: null,
  });

  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingPront, setLoadingPront] = useState(false);
  const [sendingTermo, setSendingTermo] = useState(false);
  const [deletingClient, setDeletingClient] = useState(false);
  const [deletingPront, setDeletingPront] = useState(false);

  const [confirmDeleteClient, setConfirmDeleteClient] = useState(false);
  const [confirmDeletePront, setConfirmDeletePront] = useState({
    open: false,
    id: "",
  });
  const [selectedEvolucao, setSelectedEvolucao] = useState(null);
  const [filtroEvolucao, setFiltroEvolucao] = useState("");
  const [evolutionPage, setEvolutionPage] = useState(1);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const showNotice = useCallback(
    (payload = {}) => {
      const type = ["success", "warning", "error", "info"].includes(
        payload.type
      )
        ? payload.type
        : "info";

      const notify = toast[type] || toast.info;

      return notify({
        title: payload.title,
        message: payload.message,
        details: payload.details,
        actionLabel: payload.actionLabel,
        onAction: payload.onAction,
        duration: payload.duration,
      });
    },
    [toast]
  );

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      showNotice({
        type: "warning",
        title: "Sessão expirada",
        message: "Faça login novamente para continuar acessando os dados do cliente.",
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
        duration: 0,
      });
      return null;
    }

    return { token, userId };
  }, [navigate, showNotice]);

  const handleUnauthorized = useCallback(
    (response) => {
      if (response.status !== 401 && response.status !== 403) {
        return false;
      }

      showNotice({
        type: "warning",
        title: "Sessão expirada",
        message: "Faça login novamente para continuar.",
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
        duration: 0,
      });

      return true;
    },
    [navigate, showNotice]
  );

  const fetchProntuarios = useCallback(async () => {
    if (!clientId) return;

    const auth = authOrRedirect();
    if (!auth) return;

    setLoadingPront(true);

    try {
      const response = await fetch(`${API_BASE}/psy/prontuario/${clientId}`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      if (response.status === 404) {
        setProntuarios([]);
        return;
      }

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao carregar prontuários.");
      }
      const listaProntuarios = Array.isArray(data) ? data : [];
      setProntuarios(listaProntuarios);
    } catch (error) {
      console.error("Erro ao carregar prontuários:", error);
      showNotice({
        type: "error",
        title: "Não foi possível carregar prontuários",
        message: normalizeErrorMessage(error, "Erro ao carregar prontuários."),
        actionLabel: "Tentar novamente",
        onAction: () => fetchProntuarios(),
        duration: 0,
      });
      setProntuarios([]);
    } finally {
      setLoadingPront(false);
    }
  }, [authOrRedirect, clientId, handleUnauthorized, showNotice]);

  const fetchAnamnese = useCallback(async () => {
    if (!clientId) return;

    const auth = authOrRedirect();
    if (!auth) return;

    setLoadingAnamnese(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/anamnese/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao carregar anamnese.");
      }

      setAnamneseInfo(data || null);
    } catch (error) {
      console.error("Erro ao carregar anamnese:", error);
      setAnamneseInfo(null);
      showNotice({
        type: "error",
        title: "Não foi possível carregar a anamnese",
        message: normalizeErrorMessage(error, "Erro ao carregar anamnese."),
        actionLabel: "Tentar novamente",
        onAction: () => fetchAnamnese(),
        duration: 0,
      });
    } finally {
      setLoadingAnamnese(false);
    }
  }, [authOrRedirect, clientId, handleUnauthorized, showNotice]);

  const fetchClientAppointments = useCallback(async ({ offset } = {}) => {
    if (!clientId) {
      setClientAppointments([]);
      setAppointmentsPagination((current) => ({
        ...current,
        total: 0,
        offset: 0,
        visibleFrom: 0,
        visibleTo: 0,
        hasPrevious: false,
        hasNext: false,
        previousOffset: null,
        nextOffset: null,
      }));
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setLoadingClientAppointments(true);

    try {
      const params = new URLSearchParams({
        limit: "5",
      });

      if (offset !== undefined && offset !== null) {
        params.set("offset", String(Math.max(0, Number(offset) || 0)));
      }

      const response = await fetch(
        `${API_BASE}/psy/client/${encodeURIComponent(clientId)}/agendamentos?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            Accept: "application/json",
          },
        }
      );

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(
          data?.message || "Erro ao buscar a agenda do cliente."
        );
      }

      setClientAppointments(
        Array.isArray(data?.agendamentos) ? data.agendamentos : []
      );

      setAppointmentsPagination({
        total: Number(data?.paginacao?.total || 0),
        offset: Number(data?.paginacao?.offset || 0),
        limit: Number(data?.paginacao?.limit || 5),
        visibleFrom: Number(data?.paginacao?.visibleFrom || 0),
        visibleTo: Number(data?.paginacao?.visibleTo || 0),
        hasPrevious: data?.paginacao?.hasPrevious === true,
        hasNext: data?.paginacao?.hasNext === true,
        previousOffset: data?.paginacao?.previousOffset ?? null,
        nextOffset: data?.paginacao?.nextOffset ?? null,
      });
    } catch (error) {
      console.error("Erro ao buscar agenda do cliente:", error);
      setClientAppointments([]);
      showNotice({
        type: "error",
        title: "Não foi possível carregar a agenda do cliente",
        message: normalizeErrorMessage(
          error,
          "Erro de conexão ao buscar os agendamentos."
        ),
      });
    } finally {
      setLoadingClientAppointments(false);
    }
  }, [authOrRedirect, clientId, handleUnauthorized, showNotice]);

  const buscarClienteAtualizado = useCallback(async () => {
    if (!clientId) return;

    const auth = authOrRedirect();
    if (!auth) return;

    setLoadingClient(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/client/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao atualizar dados do cliente.");
      }

      setClient(data);
      showNotice({
        type: "success",
        title: "Dados atualizados",
        message: "As informações do cliente foram atualizadas com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      showNotice({
        type: "error",
        title: "Não foi possível atualizar",
        message: normalizeErrorMessage(error, "Erro de conexão ao buscar cliente."),
        actionLabel: "Tentar novamente",
        onAction: () => buscarClienteAtualizado(),
        duration: 0,
      });
    } finally {
      setLoadingClient(false);
    }
  }, [authOrRedirect, clientId, handleUnauthorized, showNotice]);

  useEffect(() => {
    if (!clientId) {
      navigate("/clients", { replace: true });
      return;
    }

    const plano = String(readAuth(AUTH_KEYS.PLANO) || "").toLowerCase();
    setIsPremium(plano.includes("premium"));

    fetchProntuarios();
    fetchAnamnese();
    fetchClientAppointments();
  }, [
    clientId,
    fetchAnamnese,
    fetchClientAppointments,
    fetchProntuarios,
    navigate,
  ]);

  useEffect(() => {
    if (!clientId) return;

    const refreshProntuarios = Boolean(location.state?.refreshProntuarios);
    const refreshSessions = Boolean(location.state?.refreshSessions);

    if (!refreshProntuarios && !refreshSessions) return;

    if (refreshProntuarios) {
      fetchProntuarios();
    }

    if (refreshSessions) {
      fetchClientAppointments();
      showNotice({
        type: "success",
        title: "Sessão registrada",
        message:
          "A sessão foi lançada com sucesso e já está disponível nos controles financeiro e de cobrança.",
      });
    }

    navigate(location.pathname, {
      replace: true,
      state: { clientData: client },
    });
  }, [
    clientId,
    client,
    fetchClientAppointments,
    fetchProntuarios,
    location.pathname,
    location.state?.refreshProntuarios,
    location.state?.refreshSessions,
    navigate,
    showNotice,
  ]);

  useEffect(() => {
    setEvolutionPage(1);
  }, [filtroEvolucao, prontuarios]);

  const voltarLista = () => {
    navigate("/clients", { replace: true });
  };

  const enviarTermoCompromisso = async () => {
    const auth = authOrRedirect();
    if (!auth || !clientId) return;

    const termoPayload = {
      clienteId: clientId,
    };

    setSendingTermo(true);

    try {
      const response = await fetch(`${API_BASE}/psy/email/termo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(termoPayload),
      });

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao enviar termo.");
      }

      const feedback = buildCommunicationFeedback(
        data,
        data?.message || "Termo processado pelos canais disponíveis."
      );

      showNotice({
        type: feedback.tone,
        title:
          feedback.tone === "warning"
            ? "Termo enviado com atenção"
            : feedback.tone === "error"
              ? "Falha nos canais do termo"
              : "Termo enviado",
        message:
          feedback.message ||
          data?.message ||
          "Termo processado pelos canais disponíveis.",
      });
    } catch (error) {
      console.error("Erro ao enviar termo:", error);
      showNotice({
        type: "error",
        title: "Não foi possível enviar o termo",
        message: normalizeErrorMessage(error, "Erro de conexão ao enviar termo."),
        actionLabel: "Tentar novamente",
        onAction: () => enviarTermoCompromisso(),
        duration: 0,
      });
    } finally {
      setSendingTermo(false);
    }
  };

  const excluirProntuario = async (id) => {
    const auth = authOrRedirect();
    if (!auth || !id) return;

    setDeletingPront(true);

    try {
      const response = await fetch(`${API_BASE}/psy/prontuario/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao excluir prontuário.");
      }

      showNotice({
        type: "success",
        title: "Prontuário excluído",
        message: "O registro clínico foi removido com sucesso.",
      });
      fetchProntuarios();
    } catch (error) {
      console.error("Erro ao excluir prontuário:", error);
      showNotice({
        type: "error",
        title: "Não foi possível excluir",
        message: normalizeErrorMessage(error, "Erro ao excluir prontuário."),
      });
    } finally {
      setDeletingPront(false);
    }
  };

  const excluirCliente = async () => {
    const auth = authOrRedirect();
    if (!auth || !clientId) return;

    setDeletingClient(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/client/${clientId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      saveNewToken(response);

      if (handleUnauthorized(response)) return;

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao excluir cliente.");
      }

      showNotice({
        type: "success",
        title: "Cliente excluído",
        message: "O cliente foi removido da sua base com sucesso.",
      });

      navigate("/clients", {
        replace: true,
        state: { refresh: true },
      });
    } catch (error) {
      console.error("Erro ao excluir cliente:", error);
      showNotice({
        type: "error",
        title: "Não foi possível excluir o cliente",
        message: normalizeErrorMessage(error, "Erro de conexão ao excluir cliente."),
      });
    } finally {
      setDeletingClient(false);
    }
  };

  const editarCliente = () => {
    navigate("/clients/edit", { state: { clientData: client } });
  };

  const lancarSessao = () => {
    if (!clientId || !client) {
      showNotice({
        type: "warning",
        title: "Cliente não identificado",
        message: "Não foi possível abrir o lançamento da sessão para este cliente.",
      });
      return;
    }

    navigate("/launch-session", {
      state: {
        clienteId: clientId,
        clientId,
        nome: client?.nome || "Cliente",
        nomeCliente: client?.nome || "Cliente",
        cliente: client,
        clientData: client,
      },
    });
  };

  const abrirAnamnese = () => {
    navigate("/clients/anamnese", {
      state: {
        clienteId: clientId,
        cliente: client,
        clientData: client,
        anamnese: anamneseInfo?.data || null,
      },
    });
  };

  const addProntuario = () => {
    const prontuarioAtualLocal = prontuarios[0] || null;
    const temProntuarioLocal = prontuarios.length > 0;

    navigate("/prontuario/new", {
      state: {
        clienteId: clientId,
        nome: client?.nome,
        ultimaSessao: client?.ultimaSessao,
        clientData: client,
        modo: temProntuarioLocal ? "nova-evolucao" : "abrir-prontuario",
        hasProntuario: temProntuarioLocal,
        numeroProntuario: prontuarioAtualLocal?.numero || null,
        prontuarioId: prontuarioAtualLocal?.prontuarioId || null,
        totalEvolucoes: prontuarios.length,
      },
    });
  };

  const editProntuario = (prontuario) => {
    navigate("/prontuario/edit", {
      state: {
        clienteId: clientId,
        nome: client?.nome,
        ultimaSessao: client?.ultimaSessao,
        clientData: client,
        modo: "editar-evolucao",
        _idProntuario: prontuario?._id,
        evolucaoId: prontuario?._id,
        dataAtendimento: prontuario?.dataAtendimento,
        observacoes: prontuario?.observacoes,
        recomendacoes: prontuario?.recomendacoes,
        resumoSessao: prontuario?.resumoSessao,
        origem: prontuario?.origem,
        automatica: prontuario?.automatica,
        sessaoId: prontuario?.sessaoId,
        agendamentoId: prontuario?.agendamentoId,
        numero: prontuario?.numero,
        numeroEvolucao: prontuario?.numeroEvolucao,
        hasProntuario: true,
        numeroProntuario: prontuario?.numero,
        prontuarioId: prontuario?.prontuarioId || null,
        totalEvolucoes: prontuarios.length,
      },
    });
  };

  if (!client) return null;

  const menorDeIdade = client?.menorDeIdade ? "Sim" : "Não";
  const pagadorEhCliente = client?.pagadorEhCliente === false ? "Não" : "Sim";
  const dataInicioTerapia = formatDateOnlyBR(client?.dataInicioTerapia);
  const dataFimTerapia = formatDateOnlyBR(client?.dataFimTerapia);
  const contatosEmergencia = getEmergencyContacts(client);
  const totalContatosEmergencia = contatosEmergencia.length;

  const atendimentoCasal =
    client?.atendimentoCasal === true;

  const conjugeNome =
    String(
      client?.conjuge?.nome || ""
    ).trim();

  const conjugeTipo =
    capitalizeFirst(
      client?.conjuge?.tipo || ""
    );

  const conjugeCpf =
    maskCPF(
      client?.conjuge?.cpf
    );

  const totalClientAppointments =
    Number(appointmentsPagination?.total || 0);

  const anamneseExiste = Boolean(anamneseInfo?.existe && anamneseInfo?.data);
  const anamneseData = anamneseInfo?.data || null;
  const anamneseStatusRaw = String(anamneseData?.status || "").trim().toLowerCase();
  const anamneseStatusLabel = anamneseExiste
    ? anamneseStatusRaw === "finalizada"
      ? "Finalizada"
      : anamneseStatusRaw === "reaberta"
        ? "Reaberta"
        : "Rascunho"
    : "Não iniciada";
  const anamneseProgresso = Number(anamneseData?.progresso?.percentual || 0);
  const anamneseAtualizadaEm = anamneseExiste
    ? formatDateTimeBR(anamneseData?.updatedAt || anamneseData?.createdAt)
    : "Sem registro";
  const labelAcaoAnamnese = anamneseExiste
    ? anamneseStatusRaw === "finalizada"
      ? "Visualizar anamnese"
      : "Continuar anamnese"
    : "Iniciar anamnese";
  const anamneseHint = loadingAnamnese
    ? "Carregando anamnese..."
    : anamneseExiste
      ? `${anamneseStatusLabel} • ${anamneseProgresso}% preenchida`
      : "Preenchimento clínico inicial em fases";

  const evolucoes = Array.isArray(prontuarios) ? prontuarios : [];
  const totalEvolucoes = evolucoes.length;
  const temProntuario = totalEvolucoes > 0;
  const prontuarioBase = evolucoes[0] || null;
  const latestEvolutionKey = getEvolutionKey(prontuarioBase, "");
  const primeiraEvolucao = evolucoes[totalEvolucoes - 1] || null;
  const numeroProntuario =
    prontuarioBase?.numero != null ? `Nº ${prontuarioBase.numero}` : "Não aberto";
  const ultimaEvolucaoFormatada = temProntuario
    ? formatDateTimeBR(prontuarioBase?.dataAtendimento)
    : "Sem evolução";
  const dataAberturaProntuario = formatDateBR(
    prontuarioBase?.dataAbertura || primeiraEvolucao?.dataAtendimento
  );
  const totalEvolucoesLabel = `${totalEvolucoes} ${
    totalEvolucoes === 1 ? "evolução" : "evoluções"
  }`;
  const labelAcaoProntuario = temProntuario
    ? "Registrar nova evolução"
    : "Abrir prontuário";
  const dataCadastro = formatDateBR(client?.dataCadastro);
  const filtroNormalizado = String(filtroEvolucao || "").trim().toLowerCase();
  const evolucoesFiltradas = filtroNormalizado
    ? evolucoes.filter((evolucao) =>
        [
          evolucao?.numero,
          evolucao?.numeroEvolucao,
          formatDateBR(evolucao?.dataAtendimento),
          evolucao?.resumoSessao,
          evolucao?.observacoes,
          evolucao?.recomendacoes,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(filtroNormalizado)
      )
    : evolucoes;
  const totalFiltrado = evolucoesFiltradas.length;

  const totalEvolutionPages = Math.max(
    1,
    Math.ceil(
      totalFiltrado /
        EVOLUTIONS_PER_PAGE
    )
  );

  const safeEvolutionPage = Math.min(
    evolutionPage,
    totalEvolutionPages
  );

  const evolutionStartIndex =
    (safeEvolutionPage - 1) *
    EVOLUTIONS_PER_PAGE;

  const evolucoesPaginadas =
    evolucoesFiltradas.slice(
      evolutionStartIndex,
      evolutionStartIndex +
        EVOLUTIONS_PER_PAGE
    );

  const evolutionVisibleFrom =
    totalFiltrado > 0
      ? evolutionStartIndex + 1
      : 0;

  const evolutionVisibleTo =
    Math.min(
      evolutionStartIndex +
        EVOLUTIONS_PER_PAGE,
      totalFiltrado
    );

  const handleEvolutionPage = (
    nextPage
  ) => {
    const page = Math.max(
      1,
      Math.min(
        nextPage,
        totalEvolutionPages
      )
    );

    setEvolutionPage(page);

    window.requestAnimationFrame(() => {
      document
        .querySelector(
          ".cdl-section--evolucoes"
        )
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    });
  };

  const psyaInsights = [
    !anamneseExiste
      ? "A anamnese ainda não foi iniciada. Concluir esse contexto melhora a organização do acompanhamento."
      : anamneseProgresso < 100
        ? `A anamnese está ${anamneseProgresso}% preenchida. Retomar o registro evita lacunas operacionais.`
        : "A anamnese está concluída e disponível como contexto para o acompanhamento.",
    !temProntuario
      ? "O prontuário ainda não foi aberto. Registre a primeira evolução após o próximo atendimento."
      : `O prontuário possui ${totalEvolucoesLabel}. Revise o histórico antes do próximo atendimento.`,
    totalContatosEmergencia === 0
      ? "Não há contato de emergência cadastrado. Avalie complementar o perfil do cliente."
      : `${totalContatosEmergencia} contato(s) de emergência estão disponíveis no cadastro.`,
  ];

  return (
    <div className="dash-shell dash-shell--dark client-details-page">
      <div className="dash-orbs" aria-hidden="true">
        <span className="dash-orb dash-orb--a" />
        <span className="dash-orb dash-orb--b" />
        <span className="dash-orb dash-orb--c" />
        <span className="dash-orb dash-orb--d" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="dash-topbar dash-topbar--dark">
        <button
          className="dash-btn-ghost dash-iconbtn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={20} />
        </button>

        <div className="dash-brand">
          <img src={Logo} alt="PsyAssist" className="dash-brand__logo" />
          <div className="dash-brand__txt">
            <div className="dash-brand__title">Detalhes do Cliente</div>
            <div className="dash-brand__sub">
              Perfil clínico • {nomeUsuario || "Usuário"}
            </div>
          </div>
        </div>

        <div className="cdl-top-actions">
          <button
            className="dash-btn dash-btn--ghost2"
            type="button"
            onClick={voltarLista}
          >
            <ArrowLeft size={17} />
            Voltar
          </button>

          <button
            className="dash-btn dash-btn--ghost2"
            type="button"
            onClick={buscarClienteAtualizado}
            disabled={loadingClient}
          >
            <RefreshCw
              size={17}
              className={loadingClient ? "is-spinning" : ""}
            />
            {loadingClient ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="dash-main dash-main--dark">
        <div className="cdl-wrap">
          <section className="cdl-hero">
            <div className="cdl-hero__profile">
              <div className="cdl-avatar">{initials(client?.nome)}</div>

              <div className="cdl-hero__copy">
                <div className="cdl-kicker">Perfil do cliente</div>
                <h1>{client?.nome || "Cliente"}</h1>

                <div className="cdl-hero__meta">
                  <span>
                    <Mail size={14} />
                    {client?.email || "E-mail não informado"}
                  </span>

                  <span>
                    <Phone size={14} />
                    {client?.telefone || "Telefone não informado"}
                  </span>

                  <span
                    className={
                      client?.ativo === false
                        ? "cdl-hero__status cdl-hero__status--inactive"
                        : "cdl-hero__status"
                    }
                  >
                    {client?.ativo === false ? "Cadastro inativo" : "Cadastro ativo"}
                  </span>

                  {client?.dataFimTerapia ? (
                    <span className="cdl-hero__cycle-ended">
                      <CalendarDays size={14} />
                      Ciclo encerrado em {dataFimTerapia}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="cdl-hero__actions">
              <button
                className="cdl-btn cdl-btn--primary cdl-btn--launch-session"
                type="button"
                onClick={lancarSessao}
                disabled={client?.ativo === false}
                title={
                  client?.ativo === false
                    ? "Ative o cliente antes de lançar uma sessão."
                    : "Lançar sessão manual ou retroativa"
                }
              >
                <Plus size={18} />
                Lançar sessão
              </button>

              <button
                className="cdl-btn cdl-btn--ghost"
                type="button"
                onClick={enviarTermoCompromisso}
                disabled={sendingTermo}
              >
                <Send size={17} />
                {sendingTermo ? "Enviando termo..." : "Enviar termo"}
              </button>

              <button
                className="cdl-btn cdl-btn--ghost"
                type="button"
                onClick={editarCliente}
              >
                <Edit3 size={17} />
                Editar cadastro
              </button>
            </div>
          </section>

          <section className="cdl-metrics cdl-metrics--clinical">
            <MetricCard
              icon={<FolderOpen size={21} />}
              label="Prontuário"
              value={numeroProntuario}
              hint={temProntuario ? "Container clínico aberto" : "Aguardando abertura"}
              variant="blue"
            />

            <MetricCard
              icon={<BrainCircuit size={21} />}
              label="Anamnese"
              value={anamneseExiste ? `${anamneseProgresso}%` : "Não iniciada"}
              hint={anamneseHint}
              variant={anamneseExiste ? "green" : "blue"}
            />

            <MetricCard
              icon={<ClipboardList size={21} />}
              label="Evoluções"
              value={totalEvolucoes}
              hint="Sessões registradas"
              variant="green"
            />

            <MetricCard
              icon={<Clock3 size={21} />}
              label="Última evolução"
              value={ultimaEvolucaoFormatada}
              hint="Lançamento mais recente"
              variant="blue"
            />

            <MetricCard
              icon={<UserRound size={21} />}
              label="Cadastro"
              value={dataCadastro}
              hint="Entrada na base"
              variant="slate"
            />

            <MetricCard
              icon={<CalendarDays size={21} />}
              label="Início da terapia"
              value={dataInicioTerapia}
              hint="Data informada no cadastro"
              variant="green"
            />
          </section>

          {atendimentoCasal ? (
            <section className="cdl-couple-card">
              <div className="cdl-couple-card__icon">
                <UsersRound
                  size={22}
                  aria-hidden="true"
                />
              </div>

              <div className="cdl-couple-card__copy">
                <span>
                  Atendimento de casal
                </span>
                <h2>
                  {client?.nome || "Cliente"} + {conjugeNome || "Parceiro(a)"}
                </h2>
                <p>
                  O cadastro principal permanece em nome de {client?.nome || "cliente"}.
                  O parceiro(a) faz parte deste acompanhamento sem criar um segundo cliente automaticamente.
                </p>
              </div>

              <div className="cdl-couple-card__details">
                <div>
                  <span>Parceiro(a)</span>
                  <strong>
                    {conjugeNome || "Não informado"}
                  </strong>
                </div>

                <div>
                  <span>Vínculo</span>
                  <strong>
                    {conjugeTipo}
                  </strong>
                </div>

                <div>
                  <span>CPF</span>
                  <strong>
                    {conjugeCpf}
                  </strong>
                </div>
              </div>
            </section>
          ) : null}

          <section className="cdl-upcoming cdl-client-agenda">
            <div className="cdl-upcoming__head">
              <div>
                <span className="cdl-upcoming__eyebrow">
                  Agenda do cliente
                </span>
                <h2>
                  Atendimentos e agendamentos
                </h2>
                <p>
                  Consulte os registros recentes e navegue por toda a agenda deste cliente, cinco por vez.
                </p>
              </div>

              <div className="cdl-upcoming__head-actions">
                <span
                  className="cdl-upcoming__count"
                  title="Total de agendamentos do cliente"
                >
                  {loadingClientAppointments
                    ? "..."
                    : totalClientAppointments}
                </span>

                <button
                  className="cdl-btn cdl-btn--ghost"
                  type="button"
                  onClick={() =>
                    navigate(
                      "/schedule",
                      {
                        state: {
                          clienteId: clientId,
                          clientData: client,
                        },
                      }
                    )
                  }
                >
                  <CalendarDays size={17} />
                  Ver agenda completa
                </button>
              </div>
            </div>

            {loadingClientAppointments ? (
              <div className="cdl-upcoming__empty">
                <RefreshCw
                  size={19}
                  className="is-spinning"
                />
                <strong>
                  Carregando agenda do cliente...
                </strong>
              </div>
            ) : clientAppointments.length ? (
              <>
                <div className="cdl-upcoming__list">
                  {clientAppointments.map((appointment) => {
                    const date = appointment?.dataHora
                      ? new Date(appointment.dataHora)
                      : null;

                    const validDate =
                      date && !Number.isNaN(date.getTime());

                    const now = new Date();
                    const todayStart = new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate()
                    );
                    const tomorrowStart = new Date(todayStart);
                    tomorrowStart.setDate(tomorrowStart.getDate() + 1);

                    const isPast = validDate && date < now;
                    const isToday =
                      validDate &&
                      date >= todayStart &&
                      date < tomorrowStart;

                    const temporalLabel = !validDate
                      ? "Sem data"
                      : isToday
                        ? "Hoje"
                        : isPast
                          ? "Anterior"
                          : "Próximo";

                    const temporalTone = !validDate
                      ? "neutral"
                      : isToday
                        ? "today"
                        : isPast
                          ? "past"
                          : "future";

                    const dateLabel = validDate
                      ? new Intl.DateTimeFormat("pt-BR", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                          .format(date)
                          .replace(".", "")
                      : "Data não informada";

                    const timeLabel = validDate
                      ? new Intl.DateTimeFormat("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(date)
                      : "--:--";

                    const statusKey = String(
                      appointment?.status || ""
                    )
                      .trim()
                      .toLowerCase();

                    return (
                      <article
                        className={`cdl-upcoming__item cdl-upcoming__item--${temporalTone}`}
                        key={
                          appointment?._id ||
                          appointment?.id ||
                          `${appointment?.dataHora}-${appointment?.servico}`
                        }
                      >
                        <div className="cdl-upcoming__date">
                          <CalendarDays
                            size={17}
                            aria-hidden="true"
                          />

                          <div>
                            <strong>{dateLabel}</strong>
                            <span>{timeLabel}</span>
                          </div>
                        </div>

                        <div className="cdl-upcoming__service">
                          <div className="cdl-upcoming__service-top">
                            <strong>
                              {appointment?.servico ||
                                (atendimentoCasal
                                  ? "Atendimento de casal"
                                  : "Atendimento")}
                            </strong>

                            <span
                              className={`cdl-upcoming__temporal cdl-upcoming__temporal--${temporalTone}`}
                            >
                              {temporalLabel}
                            </span>
                          </div>

                          <span>
                            {appointment?.tipoSessao ||
                              appointment?.modalidade ||
                              "Atendimento agendado"}
                          </span>
                        </div>

                        <span
                          className={`cdl-upcoming__status cdl-upcoming__status--${statusKey || "pendente"}`}
                        >
                          {appointmentStatusLabel(
                            appointment?.status
                          )}
                        </span>
                      </article>
                    );
                  })}
                </div>

                <footer className="cdl-upcoming__pagination">
                  <div className="cdl-upcoming__pagination-summary">
                    <strong>
                      {appointmentsPagination.visibleFrom}–{appointmentsPagination.visibleTo}
                    </strong>
                    <span>
                      de {totalClientAppointments} agendamento(s)
                    </span>
                    <small>5 por página</small>
                  </div>

                  <div className="cdl-upcoming__pagination-controls">
                    <button
                      className="cdl-upcoming__page-btn"
                      type="button"
                      disabled={!appointmentsPagination.hasPrevious || loadingClientAppointments}
                      onClick={() =>
                        fetchClientAppointments({
                          offset: appointmentsPagination.previousOffset,
                        })
                      }
                      aria-label="Ver cinco agendamentos anteriores"
                    >
                      <ChevronLeft size={17} />
                    </button>

                    <button
                      className="cdl-upcoming__page-btn"
                      type="button"
                      disabled={!appointmentsPagination.hasNext || loadingClientAppointments}
                      onClick={() =>
                        fetchClientAppointments({
                          offset: appointmentsPagination.nextOffset,
                        })
                      }
                      aria-label="Ver próximos cinco agendamentos"
                    >
                      <ChevronRight size={17} />
                    </button>
                  </div>
                </footer>
              </>
            ) : (
              <div className="cdl-upcoming__empty">
                <CalendarDays
                  size={22}
                  aria-hidden="true"
                />

                <div>
                  <strong>
                    Nenhum agendamento encontrado
                  </strong>
                  <span>
                    Este cliente ainda não possui registros de agenda.
                  </span>
                </div>

                <button
                  className="cdl-btn cdl-btn--primary"
                  type="button"
                  onClick={() =>
                    navigate(
                      "/schedule",
                      {
                        state: {
                          clienteId: clientId,
                          clientData: client,
                        },
                      }
                    )
                  }
                >
                  <Plus size={16} />
                  Criar agendamento
                </button>
              </div>
            )}
          </section>

          <section className="cdl-record-panel">
            <div className="cdl-record-panel__copy">
              <span
                className={`cdl-record-status ${
                  temProntuario ? "cdl-record-status--open" : "cdl-record-status--empty"
                }`}
              >
                {temProntuario ? "Prontuário aberto" : "Sem prontuário"}
              </span>

              <h2>Prontuário clínico</h2>

              <p>
                O cliente mantém um prontuário único. Cada atendimento lançado
                entra como uma nova evolução no histórico clínico.
              </p>
            </div>

            <div className="cdl-record-panel__stats">
              <div>
                <span>Número</span>
                <strong>{numeroProntuario}</strong>
              </div>

              <div>
                <span>Evoluções</span>
                <strong>{totalEvolucoesLabel}</strong>
              </div>

              <div>
                <span>Abertura</span>
                <strong>{dataAberturaProntuario}</strong>
              </div>
            </div>

            <div className="cdl-record-panel__action">
              <button
                className="cdl-btn cdl-btn--primary"
                type="button"
                onClick={addProntuario}
              >
                <NotebookPen size={17} />
                {labelAcaoProntuario}
              </button>
            </div>
          </section>

          <section className="cdl-record-panel">
            <div className="cdl-record-panel__copy">
              <span
                className={`cdl-record-status ${
                  anamneseExiste ? "cdl-record-status--open" : "cdl-record-status--empty"
                }`}
              >
                {anamneseStatusLabel}
              </span>

              <h2>Anamnese clínica</h2>

              <p>
                Preenchimento inicial em fases para registrar contexto pessoal,
                saúde, medicações, família, rotina, riscos e objetivos terapêuticos.
              </p>
            </div>

            <div className="cdl-record-panel__stats">
              <div>
                <span>Status</span>
                <strong>{anamneseStatusLabel}</strong>
              </div>

              <div>
                <span>Progresso</span>
                <strong>{loadingAnamnese ? "..." : `${anamneseProgresso}%`}</strong>
              </div>

              <div>
                <span>Atualização</span>
                <strong>{anamneseAtualizadaEm}</strong>
              </div>
            </div>

            <div className="cdl-record-panel__action">
              <button
                className="cdl-btn cdl-btn--primary"
                type="button"
                onClick={abrirAnamnese}
              >
                <BrainCircuit size={17} />
                {labelAcaoAnamnese}
              </button>
            </div>
          </section>

          <section className="cdl-grid">
            <div className="cdl-card cdl-card--main">
              <div className="cdl-card__head">
                <div>
                  <div className="cdl-card__title">Dados cadastrais</div>
                  <div className="cdl-card__sub">
                    Informações essenciais para atendimento e comunicação.
                  </div>
                </div>

                <span className={`cdl-status ${client?.ativo === false ? "cdl-status--off" : ""}`}>
                  {client?.ativo === false ? "Inativo" : "Ativo"}
                </span>
              </div>

              <div className="cdl-info">
                <InfoRow label="Nome" value={client?.nome || "Não informado"} />
                <InfoRow label="Telefone" value={client?.telefone || "Não informado"} />
                <InfoRow label="E-mail" value={client?.email || "Não informado"} />
                <InfoRow label="CPF" value={formatCPF(client?.cpf)} />
                <InfoRow label="Nascimento" value={formatDateOnlyBR(client?.dataNascimento)} />
                <InfoRow
                  label="Início da terapia"
                  value={dataInicioTerapia}
                />
                <InfoRow
                  label="Fim do ciclo terapêutico"
                  value={dataFimTerapia}
                />
                <InfoRow
                  label="Contatos de emergência"
                  value={
                    totalContatosEmergencia
                      ? `${totalContatosEmergencia} contato(s)`
                      : "Nenhum cadastrado"
                  }
                />
                <InfoRow label="Menor de idade" value={menorDeIdade} />
                <InfoRow label="Pagador é cliente" value={pagadorEhCliente} />
                <InfoRow label="CPF do pagador" value={formatDocument(client?.cpfPagador)} />
                <InfoRow
                  label="CPF responsável"
                  value={formatCPF(client?.cpfResponsavel)}
                />
                <InfoRow
                  label="Cliente estrangeiro"
                  value={client?.clienteEstrangeiro ? "Sim" : "Não"}
                />
                <InfoRow
                  label="Atendimento de casal"
                  value={atendimentoCasal ? "Sim" : "Não"}
                />
              </div>
            </div>

            <aside className="cdl-side">
              <div className="cdl-card cdl-card--side">
                <div className="cdl-card__title">Ações rápidas</div>
                <div className="cdl-quick-actions">
                  <button
                    className="cdl-quick cdl-quick--launch"
                    type="button"
                    onClick={lancarSessao}
                    disabled={client?.ativo === false}
                    title={
                      client?.ativo === false
                        ? "Ative o cliente antes de lançar uma sessão."
                        : "Lançar sessão manual ou retroativa"
                    }
                  >
                    <span><Plus size={17} /></span>
                    <div className="cdl-quick__copy">
                      <strong>Lançar sessão</strong>
                      <small>Manual ou retroativa</small>
                    </div>
                  </button>

                  <button className="cdl-quick" type="button" onClick={abrirAnamnese}>
                    <span><BrainCircuit size={17} /></span>
                    {labelAcaoAnamnese}
                  </button>

                  <button className="cdl-quick cdl-quick--session" type="button" onClick={addProntuario}>
                    <span>{temProntuario ? <History size={17} /> : <Plus size={17} />}</span>
                    {labelAcaoProntuario}
                  </button>

                  <button className="cdl-quick" type="button" onClick={editarCliente}>
                    <span><Edit3 size={17} /></span>
                    Editar cadastro
                  </button>

                  <button
                    className="cdl-quick cdl-quick--danger"
                    type="button"
                    onClick={() => setConfirmDeleteClient(true)}
                  >
                    <span><Trash2 size={17} /></span>
                    Excluir cliente
                  </button>
                </div>
              </div>

              <div className="cdl-card cdl-card--side">
                <div className="cdl-card__title cdl-card__title--icon">
                  <MapPin size={17} />
                  Endereço
                </div>

                <div className="cdl-address">
                  <div>{client?.endereco || "Endereço não informado"}</div>
                  <span>
                    {[client?.bairro, client?.cidade, client?.estado]
                      .filter(Boolean)
                      .join(" • ") || "Localidade não informada"}
                  </span>
                  <small>CEP: {client?.cep || "Não informado"}</small>
                </div>
              </div>

              <div className="cdl-card cdl-card--side">
                <div className="cdl-card__title cdl-card__title--icon">
                  <ShieldCheck size={17} />
                  Contato de emergência
                </div>

                {contatosEmergencia.length ? (
                  <div className="cdl-emergency-list">
                    {contatosEmergencia.map((contato, index) => (
                      <article className="cdl-emergency-contact" key={contato?._id || index}>
                        <div className="cdl-emergency-contact__top">
                          <span>{index + 1}</span>
                          <div>
                            <strong>{getEmergencyContactTitle(contato, index)}</strong>
                            <small>{getEmergencyContactRelation(contato)}</small>
                          </div>
                        </div>

                        <div className="cdl-emergency-contact__body">
                          <a href={`tel:${onlyDigits(getEmergencyContactPhone(contato))}`}>
                            {getEmergencyContactPhone(contato)}
                          </a>

                          {contato?.email ? (
                            <a href={`mailto:${contato.email}`}>{contato.email}</a>
                          ) : null}

                          {contato?.observacao ? (
                            <p>{contato.observacao}</p>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="cdl-note">
                    Nenhum contato de emergência cadastrado.
                  </p>
                )}
              </div>
            </aside>
          </section>

          {client?.observacoes ? (
            <section className="cdl-card">
              <div className="cdl-card__title">Observações gerais</div>
              <p className="cdl-note">{client.observacoes}</p>
            </section>
          ) : null}

          {isPremium ? (
            <section className="cdl-section">
              <InsightsWidget
                title={`Sugestões para ${client?.nome || "o cliente"}`}
                insights={psyaInsights}
              />
            </section>
          ) : null}

          <section className="cdl-section cdl-section--evolucoes">
            <div className="cdl-section__head cdl-section__head--evolucoes">
              <div>
                <div className="cdl-section__title">Histórico de evoluções</div>
                <div className="cdl-section__sub">
                  Acesso completo aos lançamentos clínicos do prontuário.
                </div>
              </div>

              <div className="cdl-section__actions">
                <button
                  className="cdl-btn cdl-btn--ghost"
                  type="button"
                  onClick={fetchProntuarios}
                  disabled={loadingPront}
                >
                  <RefreshCw
                    size={17}
                    className={loadingPront ? "is-spinning" : ""}
                  />
                  {loadingPront ? "Carregando..." : "Atualizar"}
                </button>

                <button
                  className="cdl-btn cdl-btn--primary"
                  type="button"
                  onClick={addProntuario}
                >
                  <NotebookPen size={17} />
                  {labelAcaoProntuario}
                </button>
              </div>
            </div>

            {loadingPront ? (
              <div className="cdl-state">
                <RefreshCw
                  size={25}
                  className="is-spinning"
                  aria-hidden="true"
                />
                <div className="cdl-muted">
                  Carregando evoluções...
                </div>
              </div>
            ) : !temProntuario ? (
              <EmptyState
                icon={<FolderOpen size={24} />}
                title="Prontuário ainda não aberto"
                description="Abra o prontuário do cliente para registrar a primeira evolução clínica."
                actionLabel="Abrir prontuário"
                onAction={addProntuario}
              />
            ) : (
              <>
                <div className="cdl-evo-tools">
                  <div className="cdl-evo-tools__summary">
                    <strong>{totalEvolucoesLabel}</strong>
                    <span>
                      {filtroNormalizado
                        ? `${totalFiltrado} resultado(s) para o filtro aplicado`
                        : "Ordenadas da mais recente para a mais antiga"}
                    </span>

                    {totalFiltrado > 0 ? (
                      <small>
                        Exibindo{" "}
                        {evolutionVisibleFrom}–{evolutionVisibleTo} de{" "}
                        {totalFiltrado}
                      </small>
                    ) : null}
                  </div>

                  <label className="cdl-evo-search">
                    <span>
                      <Search
                        size={14}
                        aria-hidden="true"
                      />
                      Buscar evolução
                    </span>

                    <div className="cdl-evo-search__control">
                      <Search
                        size={17}
                        aria-hidden="true"
                      />

                      <input
                        type="search"
                        value={filtroEvolucao}
                        onChange={(event) =>
                          setFiltroEvolucao(
                            event.target.value
                          )
                        }
                        placeholder="Data, observação, recomendação..."
                      />

                      {filtroEvolucao ? (
                        <button
                          type="button"
                          onClick={() =>
                            setFiltroEvolucao("")
                          }
                          aria-label="Limpar busca de evoluções"
                        >
                          <X
                            size={15}
                            aria-hidden="true"
                          />
                        </button>
                      ) : null}
                    </div>
                  </label>
                </div>

                {evolucoesFiltradas.length === 0 ? (
                  <EmptyState
                    icon={<Search size={24} />}
                    title="Nenhuma evolução encontrada"
                    description="Limpe o filtro para voltar a visualizar todo o histórico clínico."
                    actionLabel="Limpar filtro"
                    onAction={() => setFiltroEvolucao("")}
                  />
                ) : (
                  <div className="cdl-pront-list cdl-evolution-timeline">
                    {evolucoesPaginadas.map((evolucao, index) => {
                      const globalIndex =
                        evolutionStartIndex + index;

                      const evolutionKey = getEvolutionKey(
                        evolucao,
                        globalIndex
                      );

                      const expanded = false;
                      const dataAtendimento = formatDateBR(
                        evolucao?.dataAtendimento
                      );

                      const isLatestEvolution =
                        evolutionKey === latestEvolutionKey;

                      const numeroEvolucao =
                        evolucao?.numeroEvolucao != null
                          ? `Evolução ${evolucao.numeroEvolucao}`
                          : `Evolução ${totalEvolucoes - globalIndex}`;
                      const resumoSessao = evolucao?.resumoSessao || "";
                      const observacoes = evolucao?.observacoes || "";
                      const recomendacoes = evolucao?.recomendacoes || "";
                      const resumoSessaoTexto = getTextPreview(resumoSessao, 300);
                      const observacoesTexto = getTextPreview(observacoes, 260);
                      const recomendacoesTexto = getTextPreview(recomendacoes, 220);
                      const origemSessao =
                        evolucao?.origem === "sessao" ||
                        Boolean(evolucao?.sessaoId);

                      return (
                        <article
                          key={evolutionKey}
                          className={`cdl-pront cdl-evolution ${
                            isLatestEvolution ? "cdl-evolution--latest" : ""
                          }`}
                        >
                          <div className="cdl-pront__top cdl-evolution__top">
                            <div>
                              <div className="cdl-pront__title cdl-evolution__title">
                                <span>{numeroEvolucao}</span>
                                <span className="cdl-dot">•</span>
                                {dataAtendimento}
                              </div>

                              <div className="cdl-pront__sub">
                                <span>Prontuário {numeroProntuario}</span>
                                {origemSessao ? (
                                  <span className="cdl-evolution-origin cdl-evolution-origin--session">
                                    Origem: sessão
                                  </span>
                                ) : (
                                  <span className="cdl-evolution-origin">
                                    Origem: manual
                                  </span>
                                )}
                                {isLatestEvolution ? (
                                  <span>• lançamento mais recente</span>
                                ) : null}
                              </div>
                            </div>

                            <div className="cdl-pront__actions">
                              <button
                                className="cdl-iconbtn cdl-iconbtn--blue"
                                type="button"
                                onClick={() => setSelectedEvolucao(evolucao)}
                                title="Visualizar evolução completa"
                                aria-label="Visualizar evolução completa"
                              >
                                <Eye size={17} />
                              </button>

                              <button
                                className="cdl-iconbtn cdl-iconbtn--blue"
                                type="button"
                                onClick={() => editProntuario(evolucao)}
                                title="Editar evolução"
                                aria-label="Editar evolução"
                              >
                                <Edit3 size={17} />
                              </button>

                              <button
                                className="cdl-iconbtn cdl-iconbtn--red"
                                type="button"
                                onClick={() =>
                                  setConfirmDeletePront({
                                    open: true,
                                    id: evolucao?._id,
                                  })
                                }
                                title="Excluir evolução"
                                aria-label="Excluir evolução"
                              >
                                <Trash2 size={17} />
                              </button>
                            </div>
                          </div>

                          <div className="cdl-pront__body cdl-evolution__body">
                            {resumoSessaoTexto ? (
                              <div className="cdl-pront__block cdl-pront__block--session-summary">
                                <strong>Resumo clínico da sessão</strong>
                                <p>{resumoSessaoTexto}</p>
                                <small>Registro original do encerramento da sessão.</small>
                              </div>
                            ) : null}

                            <div className="cdl-pront__block">
                              <strong>Observações clínicas</strong>
                              <p>{observacoesTexto || "Sem observações registradas."}</p>
                            </div>

                            <div className="cdl-pront__block">
                              <strong>Recomendações</strong>
                              <p>{recomendacoesTexto || "Sem recomendações registradas."}</p>
                            </div>
                          </div>

                          {!expanded && (
                            <button
                              className="cdl-evolution__expand"
                              type="button"
                              onClick={() => setSelectedEvolucao(evolucao)}
                            >
                              <Eye size={16} />
                              Abrir evolução completa
                            </button>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}

                {totalFiltrado > EVOLUTIONS_PER_PAGE ? (
                  <nav
                    className="cdl-evo-pagination"
                    aria-label="Paginação do histórico de evoluções"
                  >
                    <div className="cdl-evo-pagination__summary">
                      <strong>
                        {evolutionVisibleFrom}–{evolutionVisibleTo}
                      </strong>

                      <span>
                        de {totalFiltrado} evoluções
                      </span>
                    </div>

                    <div className="cdl-evo-pagination__controls">
                      <button
                        type="button"
                        onClick={() =>
                          handleEvolutionPage(
                            safeEvolutionPage - 1
                          )
                        }
                        disabled={
                          safeEvolutionPage === 1
                        }
                        aria-label="Página anterior das evoluções"
                      >
                        <ChevronLeft
                          size={17}
                          aria-hidden="true"
                        />
                        Anterior
                      </button>

                      <span>
                        Página{" "}
                        <strong>
                          {safeEvolutionPage}
                        </strong>{" "}
                        de {totalEvolutionPages}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          handleEvolutionPage(
                            safeEvolutionPage + 1
                          )
                        }
                        disabled={
                          safeEvolutionPage ===
                          totalEvolutionPages
                        }
                        aria-label="Próxima página das evoluções"
                      >
                        Próxima
                        <ChevronRight
                          size={17}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </nav>
                ) : null}
              </>
            )}
          </section>
        </div>
      </main>

      <div className="cdl-bottom-actions" aria-label="Ações rápidas do cliente">
        <button
          className="cdl-bottom-actions__btn"
          type="button"
          onClick={voltarLista}
        >
          <ArrowLeft size={17} />
          Voltar
        </button>

        <button
          className="cdl-bottom-actions__btn"
          type="button"
          onClick={abrirAnamnese}
        >
          <BrainCircuit size={17} />
          Anamnese
        </button>

        <button
          className="cdl-bottom-actions__btn cdl-bottom-actions__btn--primary"
          type="button"
          onClick={addProntuario}
        >
          <NotebookPen size={17} />
          {temProntuario ? "Nova evolução" : "Prontuário"}
        </button>
      </div>


      <EvolutionDetailModal
        open={Boolean(selectedEvolucao)}
        evolucao={selectedEvolucao}
        numeroProntuario={numeroProntuario}
        onClose={() => setSelectedEvolucao(null)}
        onEdit={(evolucao) => {
          setSelectedEvolucao(null);
          editProntuario(evolucao);
        }}
        onDelete={(evolucao) => {
          setSelectedEvolucao(null);
          setConfirmDeletePront({
            open: true,
            id: evolucao?._id,
          });
        }}
      />

      <ConfirmModal
        open={confirmDeleteClient}
        title="Excluir cliente?"
        message="Esta ação remove o cliente da sua base. Confirme apenas se você tem certeza."
        confirmText="Excluir cliente"
        danger
        loading={deletingClient}
        onClose={() => setConfirmDeleteClient(false)}
        onConfirm={() => {
          setConfirmDeleteClient(false);
          excluirCliente();
        }}
      />

      <ConfirmModal
        open={confirmDeletePront.open}
        title="Excluir evolução?"
        message="Esta ação remove a evolução clínica selecionada. O processo não poderá ser desfeito."
        confirmText="Excluir evolução"
        danger
        loading={deletingPront}
        onClose={() => setConfirmDeletePront({ open: false, id: "" })}
        onConfirm={() => {
          const id = confirmDeletePront.id;
          setConfirmDeletePront({ open: false, id: "" });

          if (id) {
            excluirProntuario(id);
          }
        }}
      />
    </div>
  );
}
